<template>
  <NuxtLink
    v-if="hasValidHref"
    v-bind="attrs"
    :to="validHref"
    :external="isExternal"
    :target="isExternal ? '_blank' : '_self'"
    :aria-label="
      isExternal && props.label
        ? `${props.label} (opens in a new tab)`
        : undefined
    "
    :aria-disabled="disabled || undefined"
    class="default-cta no-underline! inline-block group relative outline-none hover:cursor-pointer w-fit min-h-[27px]"
    :class="{
      'pointer-events-none opacity-60': disabled,
      'ps-[22px]': props.iconPosition === 'left',
      'pe-[22px]': props.iconPosition === 'right',
    }"
  >
    <slot>
      <span
        class="button-label text-underline-animation wrap-break-word font-medium transition-colors duration-300 ease-in-out"
        :class="[props.size === 'large' && 'text-body-large-medium']"
      >
        {{ props.label }}
      </span>
    </slot>

    <slot v-if="props.icon || isExternal" name="icon">
      <MBZIcon
        class="button-icon"
        :class="iconAnimationClass"
        :data-icon="renderedIcon"
        :data-position="props.iconPosition"
        :data-size="props.size ?? 'default'"
        :name="renderedIcon!"
      />
    </slot>
  </NuxtLink>

  <span v-else v-bind="attrs" class="inline-block w-fit min-h-[27px]">
    <slot>
      <span
        class="button-label wrap-break-word font-medium text-mbzuai-navy-500!"
        :class="[props.size === 'large' && 'text-body-large-medium']"
      >
        {{ props.label }}
      </span>
    </slot>
  </span>
</template>

<script setup lang="ts">
import MBZIcon, { type IconName } from '../Icon.vue';
import type { Link, CallToAction } from '~/types';

defineOptions({ inheritAttrs: false });
const attrs = useAttrs();

const props = withDefaults(
  defineProps<{
    icon?: IconName | null;
    iconPosition?: 'right' | 'left';
    url?: Link;
    label?: CallToAction['buttonLabel'];
    disabled?: boolean;
    size?: undefined | 'large';
  }>(),
  {
    icon: 'arrow',
    iconPosition: 'right',
    url: undefined,
    label: 'Learn More',
    disabled: false,
    size: undefined,
  }
);

const validHref = computed(() => {
  const href = props.url?.href?.trim() ?? '';

  if (!href || href.replace(/\s+/g, '') === '/') {
    return undefined;
  }

  return href;
});

const hasValidHref = computed(() => Boolean(validHref.value));
const isExternal = computed(() => hasValidHref.value && props.url?.external);
const renderedIcon = computed<IconName | null>(() =>
  isExternal.value ? 'externalLink' : props.icon
);
const iconAnimationClass = getIconAnimationClass(props.icon);
</script>

<style scoped>
/* White and Sand colours */
.default-cta {
  border-bottom: 3px solid transparent;
  line-height: 27px;
  font-weight: 500;
  font-size: 18px;

  &:focus-visible {
    background-color: var(--tabbed-view);
    border-bottom: 3px solid var(--p-brand-dark-navy-500);

    .text-underline-animation {
      background-image: linear-gradient(to right, black, black);
    }
  }

  .button-label.text-underline-animation {
    background-image: linear-gradient(
      to right,
      var(--p-brand-navy-400),
      var(--p-brand-navy-400)
    );
  }

  .button-label {
    color: var(--p-brand-navy-500);
    font-weight: 500;
  }

  .button-icon {
    color: var(--p-brand-sand-700);

    transform: translateY(-50%);
    position: absolute;
    min-width: 14px;
    min-height: 14px;
    top: 12px;

    &[data-position='left'] {
      left: 0;
    }

    &[data-position='right'] {
      top: 13px;
      right: 0;
    }

    &[data-size='large'] {
      min-height: 17px;
      min-width: 17px;
      right: -4px;
      top: 15px;
    }
  }

  /* Navy background */
  :is([class*='bg-mbzuai-navy'], [class*='pattern-navy']) & {
    &:focus-visible {
      border-bottom: 3px solid var(--color-white);

      .button-label {
        color: var(--p-brand-navy-500);
      }

      .button-icon {
        color: var(--p-brand-sand-700);
      }
    }

    .button-label {
      color: var(--p-brand-sand-200);
    }

    .button-icon {
      color: var(--p-brand-sand-500);
    }

    .text-underline-animation {
      background-image: linear-gradient(
        to right,
        var(--p-brand-sand-500),
        var(--p-brand-sand-500)
      );
    }
  }

  :is([class*='-pattern-white']) & {
    .button-label {
      color: var(--p-brand-navy-500);
    }

    .button-icon {
      color: var(--p-brand-sand-700);
    }

    .text-underline-animation {
      background-image: linear-gradient(
        to right,
        var(--p-brand-navy-500),
        var(--p-brand-navy-500)
      );
    }
  }
}
</style>

<style lang="scss">
html[lang='ar'] .default-cta,
html[dir='rtl'] .default-cta {
  padding-right: 0;
  padding-left: 22px;

  .button-icon {
    &[data-position='right'] {
      transform: rotate(180deg);
      right: auto;
      left: 0;
      top: 5px;
    }
  }

  &:hover .button-icon[data-icon='arrow'] {
    --tw-translate-x: -0.225rem;
  }
}
</style>
